<template>
  <div class="list-group-flush container__attachment">
    <div
      v-for="(attachment, index) in attachments"
      :key="index"
      class="list-group-item list-group-item-action align-box-row"
    >
      <div class="container__attachment-item mr-2 text-primary">
        <font-awesome-icon
          :ref="`icon__file-${index}`"
          :icon="{ prefix: 'far', iconName: 'file' }"
          class="font-size-xl mr-3 text-primary"
        />
        <div
          :ref="`text__attachment-name-${index}`"
          v-b-tooltip.hover
          :title="attachment.name"
          class="text__attachment-name"
          @click="onClickName(attachment)"
        >
          {{ attachment.name }}
        </div>
      </div>

      <div class="container__attachment-action">
        <div v-if="!enableDelete" class="container__attachment-action-viewing">
          <font-awesome-icon
            v-if="isValidToPreview(attachment.mime, attachment.ext)"
            :ref="`button__preview-${index}`"
            v-b-tooltip.hover="'Preview'"
            class="mx-auto"
            icon="search"
            @click="previewFile(attachment)"
          />
          <b-img
            :ref="`button__download-${index}`"
            v-b-tooltip.hover="'Download'"
            href="#"
            :src="require('../../assets/icon-files-list/download.svg')"
            @click="downloadAs(attachment)"
          ></b-img>
        </div>
        <font-awesome-icon
          v-else
          :ref="`button__delete-${index}`"
          icon="times"
          class="text-danger delete-file"
          @click="deleteFile(index)"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * show list of files
 * can enable/disable delete button
 *
 * example: @/examples/UploadFile.vue
 *
 * @event delete-file trigger when user upload files
 * @type {Number} index of file
 */
import { library } from '@fortawesome/fontawesome-svg-core';
import { faAngleRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faFile } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Upload from '@/models/Upload';

library.add(faAngleRight, faFile, faTimes);

export default {
  components: {
    FontAwesomeIcon
  },

  props: {
    /**
     * @type {Array<Object>}
     * @property {String} attachment.name required
     * @property {String} attachment.url required when need download
     */
    attachments: {
      type: Array,
      default: () => []
    },

    enableDelete: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    deleteFile(index) {
      this.$emit('delete-file', index);
    },

    async downloadAs(attachment) {
      try {
        const signUrl = await Upload.api().downloadFile(attachment.id);
        const response = await signUrl.response;
        const a = document.createElement('a');
        a.href = response.data.url;
        a.click();
      } catch (error) {
        console.error('Error downloading file:', error);
      }
    },

    onClickName(attachment) {
      if (this.isValidToPreview(attachment.mime, attachment.ext)) {
        return this.previewFile(attachment);
      } else {
        return this.downloadAs(attachment);
      }
    },

    isValidToPreview(mimeType, ext) {
      return (
        ext !== '.mov' &&
        (mimeType?.startsWith('image/') ||
          mimeType?.startsWith('video/') ||
          mimeType === 'application/pdf')
      );
    },

    async previewFile(attachment) {
      const fileId = attachment.id;
      const expiresIn = 60 * 60; // 60 minutes

      try {
        const {
          response: {
            data: { url }
          }
        } = await Upload.api().fetchPresignedUrl(fileId, { expiresIn });

        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.log('error', error);
      }
      this.$emit('preview-file');
    }
  }
};
</script>

<style lang="scss" scoped>
.list-group-item {
  display: grid;
  grid-template-columns: 8fr auto;
  border-bottom: 0;
  border-right: 0;
  border-left: 0;
  border-top: 0;
  margin-bottom: 1px;
  border-radius: 0;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  transition: border-radius 200ms;
  background-color: transparent;

  &:hover {
    border-radius: 0.65rem;
  }
}

.delete-file {
  cursor: pointer;
}

.container__attachment-item {
  display: flex;
  align-items: center;
  overflow: hidden;
}

.text__attachment-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.container__attachment-action-viewing {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

.container__attachment-action-viewing > * {
  cursor: pointer;
  width: 1rem;
  color: var(--primary);
}
</style>
